import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { useParams, useNavigate, Link } from "react-router-dom";
import { THEME_COLORS } from "../../ThemeConfig";
import { PatientConfig } from "../../axiosConfig";
import { Context } from "../../contexts/Store";
import { AuthModalContext } from "../../contexts/AuthModalContext";
import Lottie from "lottie-react";
import LoaderLottie from "../../assets/lottie/LoaderLottie.json";
import successTickIcon from "../../assets/icons/success-tick.svg";

// Logo-themed light theme (green + blue, same as rest of site)

// Static content overrides (optional)
const CONCERN_DETAIL_OVERRIDES = {
  "work-life-balance": {
    title: "Work-Life Related Concerns",
    whatItIs: "Managing professional pressures while maintaining personal wellness is a common challenge in today's environment.",
    commonSigns: [
      "Signs of burnout",
      "Difficulty disconnecting",
      "Reduced productivity",
      "Job-related irritability",
    ],
    howWeHelp: "Through our secure platform, Mindmitra provides access to specialized experts who understand your unique journey. We offer evidence-based interventions and a judgment-free space to heal.",
  },
};

// Decorative swirl/thought icon for "What it is" section (uses primary teal)
const WhatItIsIcon = () => (
  <svg width="100%" height="160" viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M100 20C80 40 60 60 40 80C60 100 80 120 100 140C120 120 140 100 160 80C140 60 120 40 100 20Z" stroke={THEME_COLORS.primary} strokeWidth="2" fill="none" opacity="0.5"/>
    <path d="M100 40C85 55 70 70 55 85C70 100 85 115 100 130C115 115 130 100 145 85C130 70 115 55 100 40Z" stroke={THEME_COLORS.primary} strokeWidth="1.5" fill="none" opacity="0.35"/>
    <path d="M100 60C90 70 80 80 70 90C80 100 90 110 100 120C110 110 120 100 130 90C120 80 110 70 100 60Z" stroke={THEME_COLORS.primary} strokeWidth="1" fill="none" opacity="0.25"/>
  </svg>
);

export default function ConcernDetailPage() {
  const { id } = useParams();
  const { state, dispatch } = useContext(Context);
  const { openLoginModal } = useContext(AuthModalContext);
  const auth = state.user_details?.is_verified;
  const navigate = useNavigate();
  const [concern, setConcern] = useState(null);
  const [loading, setLoading] = useState(true);

  const slugFromId = id && String(id).replace(/\s+/g, "-").toLowerCase();
  const hasOverride = slugFromId && CONCERN_DETAIL_OVERRIDES[slugFromId];

  useEffect(() => {
    if (hasOverride) {
      setLoading(false);
      return;
    }
    const headers = {};
    if (state.user_details?.access_token) {
      headers.Authorization = `Bearer ${state.user_details.access_token}`;
    }
    PatientConfig.get("api/v1/experts/area-of-expertises/", { headers })
      .then((res) => {
        const list = res.data?.data || [];
        const found = list.find(
          (c) => String(c.id) === String(id) || (c.slug && c.slug === id)
        );
        setConcern(found || null);
      })
      .catch(() => setConcern(null))
      .finally(() => setLoading(false));
  }, [id, state.user_details?.access_token, hasOverride]);

  const handleBookNow = () => {
    if (auth) {
      if (concern) {
        dispatch({ type: "ADD_SESSION", new_session: { concern: concern.id } });
      }
      navigate("/create-session");
    } else {
      openLoginModal?.();
    }
  };

  const slug =
    concern?.slug ||
    (concern?.title || concern?.name || "")
      .toLowerCase()
      .replace(/\s+/g, "-");
  const override =
    CONCERN_DETAIL_OVERRIDES[slug] ||
    (slugFromId ? CONCERN_DETAIL_OVERRIDES[slugFromId] : null);
  const pageTitle = override?.title || concern?.name || concern?.title || "Concern";
  const whatItIs = override?.whatItIs || concern?.description || "";
  const commonSigns =
    override?.commonSigns ||
    (concern?.common_signs
      ? Array.isArray(concern.common_signs)
        ? concern.common_signs
        : [concern.common_signs]
      : []);
  const howWeHelp =
    override?.howWeHelp ||
    "Through our secure platform, Mindmitra provides access to specialized experts who understand your unique journey. We offer evidence-based interventions and a judgment-free space to heal.";

  if (loading) {
    return (
      <Main>
        <LoaderWrap>
          <Lottie animationData={LoaderLottie} style={{ width: 80, height: 80 }} />
        </LoaderWrap>
      </Main>
    );
  }

  if (!concern && !hasOverride) {
    return (
      <Main>
        <Section>
          <NotFound>Concern not found.</NotFound>
          <BackLink to="/concerns">← Back to concerns</BackLink>
        </Section>
      </Main>
    );
  }

  return (
    <Main>
      <Section>
        <BackLink to="/concerns">← Back to concerns</BackLink>
        <PageTitle>{pageTitle}</PageTitle>

        {/* What it is - two column */}
        <Block>
          <BlockTitleAccent>What it is</BlockTitleAccent>
          <WhatItIsRow>
            <WhatItIsText>{whatItIs}</WhatItIsText>
            <WhatItIsGraphic>
              <WhatItIsIcon />
            </WhatItIsGraphic>
          </WhatItIsRow>
        </Block>

        {/* Common Signs - 2x2 grid with checkmarks */}
        {commonSigns.length > 0 && (
          <Block>
            <BlockTitleAccent>Common signs</BlockTitleAccent>
            <SignsGrid>
              {commonSigns.map((item, i) => (
                <SignCard key={i}>
                  <SignIcon src={successTickIcon} alt="" />
                  <SignText>{item}</SignText>
                </SignCard>
              ))}
            </SignsGrid>
          </Block>
        )}

        {/* How Mindmitra can help */}
        <Block>
          <BlockTitleWhite>How Mindmitra can help</BlockTitleWhite>
          <BlockText>{howWeHelp}</BlockText>
        </Block>

        <CtaWrap>
          <CtaButton onClick={handleBookNow}>Book session</CtaButton>
        </CtaWrap>
      </Section>
    </Main>
  );
}

const Main = styled.main`
  min-height: calc(100vh - 70px);
  background: ${THEME_COLORS.detail_page_gradient};
  color: ${THEME_COLORS.text_title};
`;
const Section = styled.section`
  padding: 32px 24px 48px;
  max-width: 900px;
  margin: 0 auto;
  box-sizing: border-box;
  @media all and (max-width: 768px) {
    padding: 24px 16px 40px;
  }
`;
const LoaderWrap = styled.div`
  min-height: calc(100vh - 70px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${THEME_COLORS.detail_page_gradient};
`;
const BackLink = styled(Link)`
  display: inline-block;
  font-family: "DM_sans";
  font-size: 14px;
  color: ${THEME_COLORS.primary};
  text-decoration: none;
  margin-bottom: 20px;
  &:hover {
    text-decoration: underline;
  }
`;
const PageTitle = styled.h1`
  font-family: "DM_sans_medium";
  font-size: 28px;
  color: ${THEME_COLORS.text_title};
  margin: 0 0 32px 0;
  letter-spacing: -0.02em;
  @media all and (max-width: 768px) {
    font-size: 22px;
    margin-bottom: 24px;
  }
`;
const Block = styled.div`
  margin-bottom: 36px;
`;
const BlockTitleAccent = styled.h2`
  font-family: "DM_sans_medium";
  font-size: 14px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${THEME_COLORS.primary};
  margin: 0 0 16px 0;
`;
const BlockTitleWhite = styled.h2`
  font-family: "DM_sans_medium";
  font-size: 18px;
  color: ${THEME_COLORS.text_title};
  margin: 0 0 12px 0;
`;
const WhatItIsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 180px;
  gap: 32px;
  align-items: center;
  @media all and (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;
const WhatItIsText = styled.p`
  font-family: "DM_sans";
  font-size: 16px;
  line-height: 1.65;
  color: ${THEME_COLORS.text_para};
  margin: 0;
`;
const WhatItIsGraphic = styled.div`
  width: 100%;
  max-width: 180px;
  margin: 0 auto;
`;
const SignsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  @media all and (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;
const SignCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: ${THEME_COLORS.detail_card_bg};
  border-radius: 12px;
  border: 1px solid ${THEME_COLORS.detail_card_border};
  backdrop-filter: blur(8px);
`;
const SignIcon = styled.img`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;
const SignText = styled.span`
  font-family: "DM_sans";
  font-size: 15px;
  color: ${THEME_COLORS.text_title};
  line-height: 1.4;
`;
const BlockText = styled.p`
  font-family: "DM_sans";
  font-size: 16px;
  line-height: 1.65;
  color: ${THEME_COLORS.text_para};
  margin: 0;
`;
const CtaWrap = styled.div`
  margin-top: 40px;
  text-align: center;
`;
const CtaButton = styled.button`
  padding: 14px 36px;
  background: ${THEME_COLORS.chips_green_on_container};
  color: ${THEME_COLORS.white};
  font-family: "DM_sans_medium";
  font-size: 16px;
  letter-spacing: 0.02em;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  box-shadow: 0 2px 12px rgba(29, 166, 135, 0.3);
  &:hover {
    opacity: 0.95;
    transform: translateY(-1px);
  }
`;
const NotFound = styled.p`
  font-family: "DM_sans";
  font-size: 16px;
  color: ${THEME_COLORS.text_para};
  text-align: center;
  margin-bottom: 16px;
`;
